import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Client-only UI state: shell chrome and transient toasts. */

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  title: string;
  body?: string;
  kind: ToastKind;
}

export interface UiState {
  /** Mobile drawer open/closed. Desktop is always visible. */
  sidebarOpen: boolean;
  /** Desktop rail vs full sidebar. */
  sidebarCollapsed: boolean;
  toasts: Toast[];
}

const initialState: UiState = {
  sidebarOpen: false,
  sidebarCollapsed: false,
  toasts: [],
};

let toastSeq = 0;

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapsed(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    toastPushed: {
      reducer(state, action: PayloadAction<Toast>) {
        state.toasts.push(action.payload);
      },
      prepare(title: string, body?: string, kind: ToastKind = 'success') {
        toastSeq += 1;
        return { payload: { id: `t${toastSeq}`, title, body, kind } satisfies Toast };
      },
    },
    toastDismissed(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  toastPushed,
  toastDismissed,
} = uiSlice.actions;
export default uiSlice.reducer;
